import { useState, useEffect, useRef } from 'react';
import { X, Home, Volume2, VolumeX, Smartphone, RotateCcw } from 'lucide-react';
import ExerciseCopyright from './ExerciseCopyright';
import ExerciseTopLogo from './ExerciseTopLogo';

// User-provided audio files
const SOUNDS = {
  inhale: 'https://customer-assets.emergentagent.com/job_inhalehub/artifacts/k71skhxh_a_melodic_atmospheri_%234-1770816945546.mp3',
  exhale: 'https://customer-assets.emergentagent.com/job_inhalehub/artifacts/htwc7k9a_a_melodic_atmospheri_%232-1770816963803.mp3'
};

const HapticBreathingModal = ({ isOpen, onClose }) => {
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isCalibrating, setIsCalibrating] = useState(false);
  const [calibrationProgress, setCalibrationProgress] = useState(0);
  const [currentPhase, setCurrentPhase] = useState('still');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [cyclesCompleted, setCyclesCompleted] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');
  const [tiltAngle, setTiltAngle] = useState(0);
  const [soundsLoaded, setSoundsLoaded] = useState(false);
  
  const stateRef = useRef({
    isActive: false,
    calibrationCount: 0,
    baselinePitch: null,
    pitchHistory: [],
    currentDirection: 'still',
    lastPeakPitch: null,
    lastTroughPitch: null,
    lastMovementTime: 0, // Track when we last detected movement
    soundEnabled: true
  });
  
  // Audio refs
  const inhaleAudioRef = useRef(null);
  const exhaleAudioRef = useRef(null);
  const timerRef = useRef(null);
  const motionHandlerRef = useRef(null);
  const fadeIntervalRef = useRef(null);

  // Preload audio files
  useEffect(() => {
    const loadSounds = async () => {
      try {
        inhaleAudioRef.current = new Audio(SOUNDS.inhale);
        exhaleAudioRef.current = new Audio(SOUNDS.exhale);
        
        inhaleAudioRef.current.loop = true;
        exhaleAudioRef.current.loop = true;
        
        inhaleAudioRef.current.volume = 0;
        exhaleAudioRef.current.volume = 0;
        
        inhaleAudioRef.current.load();
        exhaleAudioRef.current.load();
        
        await Promise.all([
          new Promise(resolve => {
            inhaleAudioRef.current.oncanplaythrough = resolve;
            setTimeout(resolve, 3000);
          }),
          new Promise(resolve => {
            exhaleAudioRef.current.oncanplaythrough = resolve;
            setTimeout(resolve, 3000);
          })
        ]);
        
        setSoundsLoaded(true);
      } catch (error) {
        console.error('Error loading sounds:', error);
        setSoundsLoaded(true);
      }
    };
    
    loadSounds();
    
    return () => {
      if (inhaleAudioRef.current) {
        inhaleAudioRef.current.pause();
        inhaleAudioRef.current = null;
      }
      if (exhaleAudioRef.current) {
        exhaleAudioRef.current.pause();
        exhaleAudioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    stateRef.current.soundEnabled = soundEnabled;
  }, [soundEnabled]);

  // Fade audio in
  const fadeIn = (audio, targetVolume = 0.7, duration = 400) => {
    if (!audio || !stateRef.current.soundEnabled) return;
    
    if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    
    audio.currentTime = 0;
    audio.volume = 0;
    audio.play().catch(e => console.log('Play blocked:', e));
    
    const steps = 20;
    const stepTime = duration / steps;
    const volumeStep = targetVolume / steps;
    let step = 0;
    
    fadeIntervalRef.current = setInterval(() => {
      step++;
      if (audio) {
        audio.volume = Math.min(targetVolume, volumeStep * step);
      }
      if (step >= steps) {
        clearInterval(fadeIntervalRef.current);
        fadeIntervalRef.current = null;
      }
    }, stepTime);
  };

  // Fade audio out
  const fadeOut = (audio, duration = 500) => {
    if (!audio) return;
    
    const startVol = audio.volume;
    if (startVol === 0) {
      audio.pause();
      return;
    }
    
    const steps = 20;
    const stepTime = duration / steps;
    const volumeStep = startVol / steps;
    let step = 0;
    
    const interval = setInterval(() => {
      step++;
      if (audio) {
        audio.volume = Math.max(0, startVol - volumeStep * step);
      }
      if (step >= steps) {
        clearInterval(interval);
        if (audio) audio.pause();
      }
    }, stepTime);
  };

  // Start inhale sound (idempotent — won't restart if already playing)
  const startInhaleSound = () => {
    if (!soundsLoaded || !stateRef.current.soundEnabled) return;
    const a = inhaleAudioRef.current;
    const alreadyPlaying = a && !a.paused && a.volume > 0.3;
    if (alreadyPlaying) {
      // Just make sure exhale is faded out and inhale is at target volume.
      fadeOut(exhaleAudioRef.current, 300);
      return;
    }
    fadeOut(exhaleAudioRef.current, 300);
    fadeIn(inhaleAudioRef.current, 0.6, 400);
  };

  // Start exhale sound (idempotent — won't restart if already playing)
  const startExhaleSound = () => {
    if (!soundsLoaded || !stateRef.current.soundEnabled) return;
    const a = exhaleAudioRef.current;
    const alreadyPlaying = a && !a.paused && a.volume > 0.3;
    if (alreadyPlaying) {
      fadeOut(inhaleAudioRef.current, 300);
      return;
    }
    fadeOut(inhaleAudioRef.current, 300);
    fadeIn(exhaleAudioRef.current, 0.7, 400);
  };

  // Stop all sounds
  const stopAllSounds = () => {
    fadeOut(inhaleAudioRef.current, 400);
    fadeOut(exhaleAudioRef.current, 400);
  };

  // IMMEDIATE silence — no fade. Used during breath pauses where we need
  // the music to cut *now*, not in 400 ms. Also neutralises any lingering
  // fade intervals so a resumed cycle starts cleanly from 0 volume.
  const forceSilent = () => {
    if (fadeIntervalRef.current) {
      clearInterval(fadeIntervalRef.current);
      fadeIntervalRef.current = null;
    }
    if (inhaleAudioRef.current) {
      inhaleAudioRef.current.volume = 0;
      try { inhaleAudioRef.current.pause(); } catch { /* ignore */ }
    }
    if (exhaleAudioRef.current) {
      exhaleAudioRef.current.volume = 0;
      try { exhaleAudioRef.current.pause(); } catch { /* ignore */ }
    }
  };

  // Calculate pitch angle from accelerometer (tilt around X axis)
  // Phone on belly, top toward head: pitch changes when belly rises/falls
  const calculatePitch = (acc) => {
    if (!acc || acc.y === null || acc.z === null) return null;
    // Pitch = angle of tilt (positive when top tilts back/up)
    return Math.atan2(-acc.y, acc.z) * (180 / Math.PI);
  };

  // Motion handler using TILT (pitch) instead of Z position
  const createMotionHandler = () => {
    return (event) => {
      const state = stateRef.current;
      if (!state.isActive) return;
      
      const acc = event.accelerationIncludingGravity;
      if (!acc) {
        setDebugInfo('Pas de données');
        return;
      }
      
      const pitch = calculatePitch(acc);
      if (pitch === null) {
        setDebugInfo('Calcul pitch impossible');
        return;
      }
      
      const CALIBRATION_COUNT = 20;
      
      // Calibration: establish baseline pitch
      if (state.calibrationCount < CALIBRATION_COUNT) {
        state.calibrationCount++;
        if (state.baselinePitch === null) {
          state.baselinePitch = pitch;
        } else {
          state.baselinePitch = state.baselinePitch * 0.9 + pitch * 0.1;
        }
        setCalibrationProgress(Math.round((state.calibrationCount / CALIBRATION_COUNT) * 100));
        setDebugInfo(`Calibration: ${state.calibrationCount}/${CALIBRATION_COUNT}`);
        
        if (state.calibrationCount >= CALIBRATION_COUNT) {
          state.lastPeakPitch = state.baselinePitch;
          state.lastTroughPitch = state.baselinePitch;
          setIsCalibrating(false);
          setDebugInfo('✓ Prêt');
        }
        return;
      }
      
      // Keep ~1s of pitch samples (motion events fire ~60 Hz on iOS).
      state.pitchHistory.push(pitch);
      if (state.pitchHistory.length > 60) state.pitchHistory.shift();
      
      // Weighted smoothing on the latest 15 samples only (recent = heavier)
      const recent = state.pitchHistory.slice(-15);
      const smoothedPitch = recent.reduce((sum, val, idx) => {
        const weight = idx + 1;
        return sum + val * weight;
      }, 0) / recent.reduce((sum, _, idx) => sum + idx + 1, 0);
      
      // Relative pitch (how much tilted from baseline)
      const relativePitch = smoothedPitch - state.baselinePitch;
      setTiltAngle(relativePitch);
      
      // -------- PAUSE DETECTION (range-over-window) --------
      // The previous "any sample jitter > 0.3°" check was way too sensitive:
      // accelerometer noise during a breath hold constantly reset the
      // movement timer, so the exhale audio kept looping. We now check the
      // ACTUAL pitch range over the last ~800 ms. If the pitch stays within
      // a tiny band (= phone is physically still), we treat it as a true
      // breath pause and silence everything.
      const now = Date.now();
      const PAUSE_WINDOW_SAMPLES = 30;    // ~500 ms at 60 Hz
      const PAUSE_RANGE_DEG = 0.6;        // tight band = true breath hold
      let isPaused = false;
      if (state.pitchHistory.length >= PAUSE_WINDOW_SAMPLES) {
        const win = state.pitchHistory.slice(-PAUSE_WINDOW_SAMPLES);
        let mn = win[0], mx = win[0];
        for (let i = 1; i < win.length; i++) {
          if (win[i] < mn) mn = win[i];
          if (win[i] > mx) mx = win[i];
        }
        isPaused = (mx - mn) < PAUSE_RANGE_DEG;
      }
      
      // Direction detection with LARGE hysteresis to prevent interruptions.
      // 5° is wide enough to filter accidental "soubresauts" (small opposite
      // movements during inhale or exhale) while still catching real
      // breath reversals (typical breath ≈ 10-15° of pitch travel).
      const hysteresis = 5.0;
      
      let newDirection = state.currentDirection;
      
      if (state.currentDirection === 'still') {
        // From still: need SIGNIFICANT position change to restart (not just drift)
        // Check that we've moved far from baseline AND there's real movement
        const significantPosition = Math.abs(relativePitch) > 3.0;
        const clearMovement = state.pitchHistory.length >= 10 &&
          Math.abs(state.pitchHistory[state.pitchHistory.length - 1] - state.pitchHistory[state.pitchHistory.length - 10]) > 1.5;
        
        if (!isPaused && significantPosition && clearMovement) {
          if (relativePitch > 0) {
            newDirection = 'falling'; // Tilting back = exhale
            state.lastTroughPitch = smoothedPitch;
            state.lastPeakPitch = smoothedPitch;
          } else {
            newDirection = 'rising'; // Tilting forward = inhale
            state.lastPeakPitch = smoothedPitch;
            state.lastTroughPitch = smoothedPitch;
          }
        }
      } else if (state.currentDirection === 'rising') {
        // Inhale: track trough (minimum pitch)
        if (smoothedPitch < state.lastTroughPitch) {
          state.lastTroughPitch = smoothedPitch;
        }
        // Switch to exhale only when pitch clearly rises (phone tilts back)
        if (smoothedPitch > state.lastTroughPitch + hysteresis) {
          newDirection = 'falling';
          state.lastPeakPitch = smoothedPitch;
        } else if (isPaused) {
          newDirection = 'still';
        }
      } else if (state.currentDirection === 'falling') {
        // Exhale: track peak (maximum pitch)
        if (smoothedPitch > state.lastPeakPitch) {
          state.lastPeakPitch = smoothedPitch;
        }
        // Switch to inhale only when pitch clearly drops (phone tilts forward)
        if (smoothedPitch < state.lastPeakPitch - hysteresis) {
          newDirection = 'rising';
          state.lastTroughPitch = smoothedPitch;
        } else if (isPaused) {
          newDirection = 'still';
        }
      }
      
      setDebugInfo(`Pitch:${smoothedPitch.toFixed(1)}° Δ:${relativePitch.toFixed(1)}° ${state.pauseType || ''}`);
      
      // Handle direction change
      if (newDirection !== state.currentDirection) {
        const prevDirection = state.currentDirection;
        
        if (prevDirection === 'falling' && newDirection === 'rising') {
          setCyclesCompleted(c => c + 1);
        }
        
        if (newDirection === 'rising') {
          setCurrentPhase('inhale');
          state.pauseType = null;
          if (prevDirection === 'still') {
            // Coming back from a pause. Only "empty pause" (after exhale)
            // required silence — now that the patient inhales again, we
            // require a sustained inhale of at least 1 s before resuming
            // music. A tiny drift during an empty hold must NOT trigger
            // the melody.
            state.inhalePendingStart = now;
          } else {
            // Normal cycle (coming from exhale) — start sound immediately.
            state.inhalePendingStart = null;
            startInhaleSound();
          }
        } else if (newDirection === 'falling') {
          setCurrentPhase('exhale');
          state.pauseType = null;
          state.inhalePendingStart = null;
          startExhaleSound();
        } else if (newDirection === 'still') {
          // Distinguish FULL pause (after inhale → inhale music KEEPS playing)
          // from EMPTY pause (after exhale → TOTAL silence).
          state.inhalePendingStart = null;
          if (prevDirection === 'rising') {
            // End of inspiration — hold with full lungs. Keep inhale sound
            // playing continuously; it already loops, so we do nothing.
            state.pauseType = 'full';
            setCurrentPhase('inhale'); // UI stays on "inhale" look
          } else {
            // End of expiration — hold with empty lungs. SILENCE IMMEDIATELY.
            state.pauseType = 'empty';
            setCurrentPhase('still');
            forceSilent();
          }
        }
        
        state.currentDirection = newDirection;
      }
      
      // Continuous safety guard — only for EMPTY pauses (after exhale).
      // On a FULL pause (after inhale) we deliberately let the looped inhale
      // track keep playing. This is also why we gate against the direction FSM
      // failing to transition cleanly on slow devices.
      if (state.currentDirection === 'still' && state.pauseType === 'empty') {
        forceSilent();
      }
      
      // Confirm a pending inhale once it has been sustained ≥ 1 s.
      // This prevents the music from re-triggering on tiny drifts after a pause.
      if (state.currentDirection === 'rising' && state.inhalePendingStart !== null) {
        if (now - state.inhalePendingStart >= 1000) {
          state.inhalePendingStart = null;
          startInhaleSound();
        }
      }
    };
  };

  // Request permission
  const requestPermission = async () => {
    if (typeof DeviceMotionEvent === 'undefined') {
      setDebugInfo('DeviceMotion non disponible');
      setPermissionDenied(true);
      return;
    }
    
    if (typeof DeviceMotionEvent.requestPermission === 'function') {
      try {
        const permission = await DeviceMotionEvent.requestPermission();
        if (permission === 'granted') {
          setHasPermission(true);
        } else {
          setPermissionDenied(true);
        }
      } catch (error) {
        setPermissionDenied(true);
        setDebugInfo(`Erreur: ${error.message}`);
      }
    } else {
      setHasPermission(true);
    }
  };

  // Start exercise
  const startExercise = () => {
    stateRef.current = {
      isActive: true,
      calibrationCount: 0,
      baselinePitch: null,
      pitchHistory: [],
      currentDirection: 'still',
      lastPeakPitch: null,
      lastTroughPitch: null,
      lastMovementTime: Date.now(),
      inhalePendingStart: null,
      pauseType: null,
      soundEnabled: soundEnabled
    };
    
    setCalibrationProgress(0);
    setCyclesCompleted(0);
    setElapsedTime(0);
    setCurrentPhase('still');
    setTiltAngle(0);
    setIsCalibrating(true);
    setIsActive(true);
    setDebugInfo('Calibration...');
    
    // Unlock audio on mobile
    if (inhaleAudioRef.current) {
      inhaleAudioRef.current.volume = 0;
      inhaleAudioRef.current.play().then(() => inhaleAudioRef.current.pause()).catch(() => {});
    }
    if (exhaleAudioRef.current) {
      exhaleAudioRef.current.volume = 0;
      exhaleAudioRef.current.play().then(() => exhaleAudioRef.current.pause()).catch(() => {});
    }
    
    motionHandlerRef.current = createMotionHandler();
    window.addEventListener('devicemotion', motionHandlerRef.current);
    
    timerRef.current = setInterval(() => {
      setElapsedTime(t => t + 1);
    }, 1000);
  };

  // Stop exercise
  const stopExercise = () => {
    stateRef.current.isActive = false;
    setIsActive(false);
    setIsCalibrating(false);
    setCurrentPhase('still');
    stopAllSounds();
    
    if (motionHandlerRef.current) {
      window.removeEventListener('devicemotion', motionHandlerRef.current);
      motionHandlerRef.current = null;
    }
    
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  // Reset session (go back to initial state)
  const resetSession = () => {
    stopExercise();
    setElapsedTime(0);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      stateRef.current.isActive = false;
      if (motionHandlerRef.current) {
        window.removeEventListener('devicemotion', motionHandlerRef.current);
      }
      stopAllSounds();
      if (timerRef.current) clearInterval(timerRef.current);
      if (fadeIntervalRef.current) clearInterval(fadeIntervalRef.current);
    };
  }, []);

  const handleClose = () => {
    stopExercise();
    onClose();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getPhaseInfo = () => {
    switch (currentPhase) {
      case 'inhale':
        return { text: 'Inspiration', color: '#22d3ee', icon: '🌊↑' };
      case 'exhale':
        return { text: 'Expiration', color: '#f97316', icon: '🌊↓' };
      default:
        return { text: 'Prêt', color: '#64748b', icon: '···' };
    }
  };

  const phaseInfo = getPhaseInfo();

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <ExerciseCopyright />
      <div 
        className="relative w-full h-full flex flex-col"
        style={{
          background: 'linear-gradient(180deg, #0c1426 0%, #1e3a5f 50%, #0a192f 100%)'
        }}
      >
        {/* Wave background */}
        <div className="absolute inset-0 overflow-hidden">
          <svg className="absolute bottom-0 w-full h-40" viewBox="0 0 1440 320" preserveAspectRatio="none">
            <path 
              fill={phaseInfo.color}
              fillOpacity={currentPhase === 'still' ? '0.15' : '0.35'}
              d="M0,160L60,170.7C120,181,240,203,360,197.3C480,192,600,160,720,154.7C840,149,960,171,1080,181.3C1200,192,1320,192,1380,192L1440,192L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
              style={{ transition: 'fill-opacity 0.5s, fill 0.5s' }}
            />
          </svg>
        </div>

        {/* Floating Navigation Buttons */}
        <div className="absolute top-4 left-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Retour aux exercices"
          >
            <Home size={20} className="text-white" />
          </button>
        </div>
        
        <div className="absolute top-4 right-4 z-20">
          <button
            onClick={handleClose}
            className="p-3 bg-white/20 hover:bg-white/30 backdrop-blur-md rounded-full transition-colors"
            title="Fermer"
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        <ExerciseTopLogo />

        {/* Main Content */}
        <div className="flex-1 flex flex-col items-center justify-center px-4 pt-16 pb-8 relative z-10">
          
          {!hasPermission && !permissionDenied ? (
            <div className="text-center max-w-md">
              <Smartphone size={80} className="text-cyan-400 mx-auto mb-4" />
              <h3 className="text-white text-2xl font-bold mb-4">Respiration Haptique</h3>
              <p className="text-white/70 text-lg mb-6">
                Un son atmosphérique accompagne votre respiration abdominale.
              </p>
              <div className="bg-white/10 rounded-xl p-4 mb-6 text-left">
                <p className="text-white/90 text-sm mb-2">📱 <strong>Position du téléphone :</strong></p>
                <p className="text-white/70 text-sm mb-3">À plat sur le ventre, haut du téléphone vers la tête</p>
                <ul className="text-white/70 text-sm space-y-1">
                  <li>• Ventre gonfle → son d&apos;inspiration</li>
                  <li>• Ventre dégonfle → son d&apos;expiration</li>
                </ul>
              </div>
              <button 
                onClick={requestPermission} 
                disabled={!soundsLoaded}
                className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600 disabled:bg-cyan-800 text-white font-bold rounded-xl shadow-lg transition-colors"
              >
                {soundsLoaded ? 'Autoriser les capteurs' : 'Chargement sons...'}
              </button>
            </div>
          ) : permissionDenied ? (
            <div className="text-center max-w-md">
              <p className="text-red-400 text-xl mb-4">⚠️ Accès refusé</p>
              <p className="text-white/70">{debugInfo}</p>
            </div>
          ) : (
            <>
              {/* Stats */}
              <div className="flex gap-3 mb-6">
                <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-3 text-center border border-white/20">
                  <p className="text-white/70 text-xs mb-1">Temps</p>
                  <p className="text-white font-bold text-xl">{formatTime(elapsedTime)}</p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl px-4 py-3 text-center border border-white/20">
                  <p className="text-white/70 text-xs mb-1">Cycles</p>
                  <p className="text-white font-bold text-xl">{cyclesCompleted}</p>
                </div>
              </div>

              {/* Calibration */}
              {isCalibrating && (
                <div className="w-full max-w-xs mb-6">
                  <p className="text-cyan-400 text-center mb-2 font-medium">Calibration...</p>
                  <div className="h-3 bg-white/20 rounded-full overflow-hidden">
                    <div className="h-full bg-cyan-500 rounded-full transition-all" style={{ width: `${calibrationProgress}%` }} />
                  </div>
                  <p className="text-white/50 text-xs text-center mt-2">Restez immobile</p>
                </div>
              )}

              {/* Phase indicator */}
              <div 
                className="w-56 h-56 rounded-full flex flex-col items-center justify-center mb-6 transition-all duration-500"
                style={{
                  background: `radial-gradient(circle, ${phaseInfo.color}50 0%, ${phaseInfo.color}15 60%, transparent 100%)`,
                  boxShadow: currentPhase !== 'still' ? `0 0 100px ${phaseInfo.color}50` : 'none',
                  transform: currentPhase === 'inhale' ? 'scale(1.2)' : currentPhase === 'exhale' ? 'scale(0.8)' : 'scale(1)'
                }}
              >
                <span className="text-5xl mb-2">{phaseInfo.icon}</span>
                <p className="text-white font-bold text-xl">{phaseInfo.text}</p>
              </div>

              {/* Tilt indicator */}
              {isActive && !isCalibrating && (
                <div className="w-full max-w-xs mb-4">
                  <div className="h-8 bg-white/10 rounded-full overflow-hidden relative border border-white/20">
                    <div className="absolute left-1/2 top-0 bottom-0 w-px bg-white/30" />
                    <div 
                      className="absolute top-1 bottom-1 w-4 rounded-full transition-all duration-150"
                      style={{
                        left: `calc(50% + ${Math.max(-45, Math.min(45, tiltAngle * 5))}% - 8px)`,
                        backgroundColor: currentPhase === 'inhale' ? '#22d3ee' : currentPhase === 'exhale' ? '#f97316' : '#64748b'
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-white/40 text-xs mt-1">
                    <span>Expir</span>
                    <span>Inspir</span>
                  </div>
                </div>
              )}

              {/* Debug */}
              {isActive && (
                <p className="text-white/40 text-xs font-mono mb-4">{debugInfo}</p>
              )}

              {/* Controls */}
              <div className="flex gap-4">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="w-14 h-14 rounded-full bg-white/80 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                >
                  {soundEnabled ? <Volume2 className="w-6 h-6 text-slate-600" /> : <VolumeX className="w-6 h-6 text-slate-400" />}
                </button>

                {!isActive ? (
                  <button onClick={startExercise} className="px-8 py-4 bg-cyan-500 hover:bg-cyan-600 text-white font-bold rounded-xl shadow-lg transition-colors">
                    Commencer
                  </button>
                ) : (
                  <>
                    <button onClick={stopExercise} className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white font-bold rounded-xl shadow-lg transition-colors">
                      Arrêter
                    </button>
                    <button 
                      onClick={resetSession} 
                      className="w-14 h-14 rounded-full bg-white/80 shadow-lg flex items-center justify-center hover:bg-white transition-colors"
                      style={{ border: '2px solid rgba(239, 68, 68, 0.5)' }}
                      title="Réinitialiser"
                    >
                      <RotateCcw className="w-6 h-6 text-red-500" />
                    </button>
                  </>
                )}
              </div>

              {!isActive && (
                <p className="mt-6 text-white/50 text-sm text-center max-w-md">
                  Placez le téléphone à plat sur votre ventre, haut vers la tête, et respirez naturellement.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HapticBreathingModal;
